import React, { useState } from "react";
import { Check, Mail } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { submitForm } from "../lib/submit";

export default function WaitlistPage() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ first: "", country: "", email: "", honey: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || busy) return;
    setBusy(true);

    await submitForm(
      {
        first_name: form.first,
        country: form.country,
        email: form.email,
      },
      {
        subject: `FOYEVOO Waitlist Signup \u2014 ${form.email}`,
        source: "Waitlist Page",
        honeypot: form.honey,
      }
    );

    setSubmitted(true);
    setBusy(false);
    toast({ title: "You're on the list", description: "We'll be in touch before the public launch." });
  };

  return (
    <div className="bg-[#f4ede0] text-[#1a2a38] min-h-screen">
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pt-20 pb-24">
        <p className="text-xs tracking-[0.4em] uppercase text-[#d4a857] mb-4 text-center">Priority Waitlist</p>
        <h1 className="text-4xl md:text-5xl font-light text-center leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Our launch harvest is in high demand. Reserve your spot.
        </h1>
        <p className="mt-5 text-center text-[#3a4a58]">Join the waitlist and unlock early access before public release.</p>

        <ul className="mt-10 space-y-3 max-w-md mx-auto">
          {[
            "Early access before public launch",
            "Pre-launch pricing locked in",
            "Regular updates from the grove",
            "We never share your information with anyone",
          ].map((t, i) => (
            <li key={i} className="flex gap-3 items-start"><Check size={18} className="text-[#d4a857] mt-0.5" strokeWidth={2.5} /><span>{t}</span></li>
          ))}
        </ul>

        {submitted ? (
          <div className="mt-12 p-8 bg-[#ebe2cd] border border-[#4a7ba1]/30 rounded-sm text-center">
            <Mail size={28} className="mx-auto text-[#d4a857]" strokeWidth={1.4} />
            <h3 className="mt-3 text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>You're on the list.</h3>
            <p className="mt-2 text-[#3a4a58]">We'll email <span className="font-medium text-[#1e3a52]">{form.email}</span> when allocations open.</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-12 grid gap-4 max-w-md mx-auto">
            {/* Honeypot field — hidden from real users; bots fill it */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.honey}
              onChange={(e) => setForm({ ...form, honey: e.target.value })}
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              aria-hidden="true"
            />
            <input
              value={form.first}
              onChange={(e) => setForm({ ...form, first: e.target.value })}
              placeholder="First name"
              className="px-4 py-3 bg-[#ebe2cd]/50 border border-[#4a7ba1]/30 rounded-sm focus:outline-none focus:border-[#1e3a52]"
            />
            <input
              value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
              placeholder="Country *"
              required
              className="px-4 py-3 bg-[#ebe2cd]/50 border border-[#4a7ba1]/30 rounded-sm focus:outline-none focus:border-[#1e3a52]"
            />
            <input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              type="email"
              required
              placeholder="Email *"
              className="px-4 py-3 bg-[#ebe2cd]/50 border border-[#4a7ba1]/30 rounded-sm focus:outline-none focus:border-[#1e3a52]"
            />
            <button
              type="submit"
              disabled={busy}
              className="bg-[#1e3a52] hover:bg-[#142a3f] disabled:opacity-60 text-[#f4ede0] py-3 rounded-sm transition-colors"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "18px" }}
            >
              {busy ? "Submitting\u2026" : "Save your spot"}
            </button>
            <p className="text-[11px] text-[#3a4a58]/70 text-center mt-1">We never share your information with anyone.</p>
          </form>
        )}
      </section>
    </div>
  );
}
